import { useState, useEffect } from 'react';
import { TreatmentSession, SessionStatus, PaymentStatus, SessionDocument, SessionPhoto, PaymentReceipt } from '@/types/treatmentSession';
import { toast } from "sonner";
import { addDays, format, parseISO } from 'date-fns';

// Mock data helper
const generateSampleSessions = (planId: number | string): TreatmentSession[] => {
  const today = new Date();
  
  // If planId is 'all', return all sessions across plans
  if (planId === 'all') {
    const sessions = [];
    
    // Generate sessions for multiple plans
    for (let i = 1; i <= 3; i++) {
      sessions.push(...generateSampleSessions(i));
    }
    
    return sessions;
  }
  
  const planIdNum = typeof planId === 'string' ? parseInt(planId) : planId;
  
  // Create sample sessions
  return [
    {
      id: 1 + ((planIdNum - 1) * 10),
      planId: planIdNum,
      sessionNumber: 1,
      title: "Initial Consultation",
      description: "Complete diagnosis and treatment plan setup",
      date: addDays(today, -7).toISOString(),
      status: "completed",
      paymentStatus: "paid",
      doctorId: 1,
      doctorName: "Dr. James Wilson",
      doctorNotes: "Patient has good oral hygiene. Recommended standard cleaning and whitening.",
      documents: [
        {
          id: 101 + ((planIdNum - 1) * 100),
          sessionId: 1 + ((planIdNum - 1) * 10),
          title: "Initial Assessment Form",
          type: "consent",
          fileUrl: "/placeholder.svg",
          uploadedAt: addDays(today, -7).toISOString(),
          uploadedBy: "doctor"
        },
        {
          id: 102 + ((planIdNum - 1) * 100),
          sessionId: 1 + ((planIdNum - 1) * 10),
          title: "Dental X-Rays",
          type: "xray",
          fileUrl: "/placeholder.svg",
          uploadedAt: addDays(today, -7).toISOString(),
          uploadedBy: "doctor"
        }
      ],
      photos: [
        {
          id: 201 + ((planIdNum - 1) * 100),
          sessionId: 1 + ((planIdNum - 1) * 10),
          type: "before",
          fileUrl: "/placeholder.svg",
          description: "Initial teeth condition",
          uploadedAt: addDays(today, -7).toISOString(),
          uploadedBy: "doctor"
        }
      ],
      paymentReceipt: {
        id: 301 + ((planIdNum - 1) * 100),
        sessionId: 1 + ((planIdNum - 1) * 10),
        amount: 150,
        currency: "USD",
        fileUrl: "/placeholder.svg",
        uploadedAt: addDays(today, -7).toISOString(),
        uploadedBy: "clinic"
      },
      patientAttended: true
    },
    {
      id: 2 + ((planIdNum - 1) * 10),
      planId: planIdNum,
      sessionNumber: 2,
      title: "Teeth Cleaning",
      description: "Professional dental cleaning and polishing",
      date: addDays(today, -1).toISOString(),
      status: "completed",
      paymentStatus: "paid",
      doctorId: 1,
      doctorName: "Dr. James Wilson",
      doctorNotes: "Cleaning completed successfully. Patient should continue with improved brushing technique.",
      documents: [
        {
          id: 103 + ((planIdNum - 1) * 100),
          sessionId: 2 + ((planIdNum - 1) * 10),
          title: "Cleaning Procedure Form",
          type: "consent",
          fileUrl: "/placeholder.svg",
          uploadedAt: addDays(today, -1).toISOString(),
          uploadedBy: "doctor"
        }
      ],
      photos: [
        {
          id: 202 + ((planIdNum - 1) * 100),
          sessionId: 2 + ((planIdNum - 1) * 10),
          type: "before",
          fileUrl: "/placeholder.svg",
          description: "Before cleaning",
          uploadedAt: addDays(today, -1).toISOString(),
          uploadedBy: "doctor"
        },
        {
          id: 203 + ((planIdNum - 1) * 100),
          sessionId: 2 + ((planIdNum - 1) * 10),
          type: "after",
          fileUrl: "/placeholder.svg",
          description: "After cleaning",
          uploadedAt: addDays(today, -1).toISOString(),
          uploadedBy: "doctor"
        }
      ],
      paymentReceipt: {
        id: 302 + ((planIdNum - 1) * 100),
        sessionId: 2 + ((planIdNum - 1) * 10),
        amount: 200,
        currency: "USD",
        fileUrl: "/placeholder.svg",
        uploadedAt: addDays(today, -1).toISOString(),
        uploadedBy: "clinic"
      },
      patientAttended: true
    },
    {
      id: 3 + ((planIdNum - 1) * 10),
      planId: planIdNum,
      sessionNumber: 3,
      title: "Whitening Procedure",
      description: "Professional teeth whitening treatment",
      date: addDays(today, 1).toISOString(),
      status: "upcoming",
      paymentStatus: "unpaid",
      doctorId: 1,
      doctorName: "Dr. James Wilson",
      documents: [
        {
          id: 104 + ((planIdNum - 1) * 100),
          sessionId: 3 + ((planIdNum - 1) * 10),
          title: "Whitening Consent Form",
          type: "consent",
          fileUrl: "/placeholder.svg",
          uploadedAt: addDays(today, -3).toISOString(),
          uploadedBy: "clinic"
        }
      ],
      photos: [],
      patientAttended: false
    },
    {
      id: 4 + ((planIdNum - 1) * 10),
      planId: planIdNum,
      sessionNumber: 4,
      title: "Follow-up Check",
      description: "Evaluation of whitening results and touch-ups if needed",
      date: addDays(today, 14).toISOString(),
      status: "upcoming",
      paymentStatus: "unpaid",
      doctorId: 1,
      doctorName: "Dr. James Wilson",
      documents: [],
      photos: [],
      patientAttended: false
    }
  ] as TreatmentSession[];
};

export const useTreatmentSessions = (planId: number | string) => {
  const [sessions, setSessions] = useState<TreatmentSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch sessions
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        // In a real app, this would be an API call
        setIsLoading(true);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Get sample data
        const data = generateSampleSessions(planId);
        setSessions(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching treatment sessions:', err);
        setError('Failed to load treatment sessions. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSessions();
  }, [planId]);
  
  // Upload document
  const uploadDocument = async (sessionId: number, document: Omit<SessionDocument, 'id' | 'sessionId' | 'uploadedAt'>) => {
    try {
      // Simulate API request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const newDocument: SessionDocument = {
        ...document,
        id: Date.now(),
        sessionId,
        uploadedAt: new Date().toISOString()
      };
      
      setSessions(prevSessions => 
        prevSessions.map(session => 
          session.id === sessionId
            ? { ...session, documents: [...session.documents, newDocument] }
            : session
        )
      );
      
      toast.success("Document uploaded successfully");
      return newDocument;
    } catch (err) {
      console.error('Error uploading document:', err);
      toast.error("Failed to upload document");
      throw err;
    }
  };
  
  // Upload photo
  const uploadPhoto = async (sessionId: number, photo: Omit<SessionPhoto, 'id' | 'sessionId' | 'uploadedAt'>) => {
    try {
      // Simulate API request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const newPhoto: SessionPhoto = {
        ...photo,
        id: Date.now(),
        sessionId,
        uploadedAt: new Date().toISOString()
      };
      
      setSessions(prevSessions => 
        prevSessions.map(session => 
          session.id === sessionId
            ? { ...session, photos: [...session.photos, newPhoto] }
            : session
        )
      );
      
      toast.success("Photo uploaded successfully");
      return newPhoto;
    } catch (err) {
      console.error('Error uploading photo:', err);
      toast.error("Failed to upload photo");
      throw err;
    }
  };
  
  // Upload payment receipt
  const uploadPaymentReceipt = async (sessionId: number, receipt: Omit<PaymentReceipt, 'id' | 'sessionId' | 'uploadedAt'>) => {
    try {
      // Simulate API request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const newReceipt: PaymentReceipt = {
        ...receipt,
        id: Date.now(),
        sessionId,
        uploadedAt: new Date().toISOString()
      };
      
      setSessions(prevSessions => 
        prevSessions.map(session => 
          session.id === sessionId
            ? { ...session, paymentReceipt: newReceipt, paymentStatus: 'paid' }
            : session
        )
      );
      
      toast.success("Payment receipt uploaded successfully");
      return newReceipt;
    } catch (err) {
      console.error('Error uploading payment receipt:', err);
      toast.error("Failed to upload payment receipt");
      throw err;
    }
  };
  
  // Update session status
  const updateSessionStatus = async (sessionId: number, status: SessionStatus) => {
    try {
      // Simulate API request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSessions(prevSessions => 
        prevSessions.map(session => 
          session.id === sessionId
            ? { ...session, status }
            : session
        )
      );
      
      toast.success(`Session marked as ${status}`);
    } catch (err) {
      console.error('Error updating session status:', err);
      toast.error("Failed to update session status");
      throw err;
    }
  };
  
  // Update session date
  const updateSessionDate = async (sessionId: number, date: string) => {
    try {
      // Simulate API request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSessions(prevSessions => 
        prevSessions.map(session => 
          session.id === sessionId
            ? { ...session, date }
            : session
        )
      );
      
      toast.success("Session rescheduled successfully");
    } catch (err) {
      console.error('Error updating session date:', err);
      toast.error("Failed to reschedule session");
      throw err;
    }
  };
  
  // Update doctor notes
  const updateDoctorNotes = async (sessionId: number, doctorNotes: string) => {
    try {
      // Simulate API request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSessions(prevSessions => 
        prevSessions.map(session => 
          session.id === sessionId
            ? { ...session, doctorNotes }
            : session
        )
      );
      
      toast.success("Notes updated successfully");
    } catch (err) {
      console.error('Error updating doctor notes:', err);
      toast.error("Failed to update notes");
      throw err;
    }
  };
  
  // Update clinic notes
  const updateClinicNotes = async (sessionId: number, clinicNotes: string) => {
    try {
      // Simulate API request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSessions(prevSessions => 
        prevSessions.map(session => 
          session.id === sessionId
            ? { ...session, clinicNotes }
            : session
        )
      );
      
      toast.success("Notes updated successfully");
    } catch (err) {
      console.error('Error updating clinic notes:', err);
      toast.error("Failed to update notes");
      throw err;
    }
  };
  
  // Mark patient attendance
  const updatePatientAttendance = async (sessionId: number, attended: boolean) => {
    try {
      // Simulate API request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSessions(prevSessions => 
        prevSessions.map(session => 
          session.id === sessionId
            ? { ...session, patientAttended: attended }
            : session
        )
      );
      
      toast.success(attended ? "Patient marked as attended" : "Patient marked as not attended");
    } catch (err) {
      console.error('Error updating patient attendance:', err);
      toast.error("Failed to update attendance");
      throw err;
    }
  };
  
  // Add new session
  const addSession = async (session: Omit<TreatmentSession, 'id' | 'documents' | 'photos'>) => {
    try {
      // Simulate API request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const newSession: TreatmentSession = {
        ...session,
        id: Date.now(),
        documents: [],
        photos: []
      };
      
      setSessions(prevSessions => [...prevSessions, newSession]);
      
      toast.success("New session created successfully");
      return newSession;
    } catch (err) {
      console.error('Error adding session:', err);
      toast.error("Failed to create new session");
      throw err;
    }
  };
  
  // Delete document
  const deleteDocument = async (documentId: number) => {
    try {
      // Simulate API request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSessions(prevSessions => 
        prevSessions.map(session => ({
          ...session,
          documents: session.documents.filter(doc => doc.id !== documentId)
        }))
      );
      
      toast.success("Document deleted successfully");
    } catch (err) {
      console.error('Error deleting document:', err);
      toast.error("Failed to delete document");
      throw err;
    }
  };
  
  // Delete photo
  const deletePhoto = async (photoId: number) => {
    try {
      // Simulate API request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSessions(prevSessions => 
        prevSessions.map(session => ({
          ...session,
          photos: session.photos.filter(photo => photo.id !== photoId)
        }))
      );
      
      toast.success("Photo deleted successfully");
    } catch (err) {
      console.error('Error deleting photo:', err);
      toast.error("Failed to delete photo");
      throw err;
    }
  };
  
  // Check for upcoming sessions that need notifications
  const getUpcomingSessionNotifications = () => {
    const upcoming = sessions.filter(session => session.status === 'upcoming');
    const notifications = [];
    
    for (const session of upcoming) {
      const sessionDate = parseISO(session.date);
      const now = new Date();
      const diffHours = (sessionDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      
      if (diffHours <= 24 && diffHours > 23) {
        // Day before
        notifications.push({
          type: '24h',
          session,
          message: `Reminder: You have a session tomorrow at ${format(sessionDate, 'h:mm a')}`
        });
      } else if (diffHours <= 1 && diffHours > 0) {
        // Hour before
        notifications.push({
          type: '1h',
          session,
          message: `Reminder: You have a session in 1 hour at ${format(sessionDate, 'h:mm a')}`
        });
      }
    }
    
    return notifications;
  };
  
  return {
    sessions,
    isLoading,
    error,
    uploadDocument,
    uploadPhoto,
    uploadPaymentReceipt,
    updateSessionStatus,
    updateSessionDate,
    updateDoctorNotes,
    updateClinicNotes,
    updatePatientAttendance,
    addSession,
    deleteDocument,
    deletePhoto,
    getUpcomingSessionNotifications
  };
};
